import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { LoadingSpinner } from '../shared';
import { TrainingNavItem } from '../../services/trainingService';
import { markdownSx } from './markdownStyles';
import { buildMarkdownComponents } from './markdownComponents';

/**
 * Stable empty default. An inline `docs = []` would be a fresh array identity
 * on every render, busting the useMemo below and remounting every heading,
 * link and code element in the document each time.
 */
const NO_DOCS: TrainingNavItem[] = [];

interface MarkdownContentProps {
  content: string;
  loading?: boolean;
  error?: string | null;
  onNavigateToDoc?: (docId: string) => void;
  docs?: TrainingNavItem[];
}

/**
 * Renders one manual. Styling lives in markdownStyles, element renderers in
 * markdownComponents, so this file is just the three states and the wiring.
 */
export const MarkdownContent: React.FC<MarkdownContentProps> = ({
  content,
  loading = false,
  error = null,
  onNavigateToDoc,
  docs = NO_DOCS,
}) => {
  // Rebuilding this map on every render would remount every heading, link and
  // code element in the document on each keystroke elsewhere in the drawer.
  const components = useMemo(
    () => buildMarkdownComponents(docs, onNavigateToDoc),
    [docs, onNavigateToDoc]
  );

  if (loading) {
    return <LoadingSpinner message="Loading document..." />;
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">
          Error Loading Document
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          {error}
        </Typography>
      </Box>
    );
  }

  if (!content) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">No content available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={markdownSx}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </Box>
  );
};
